/**
 * Sorting for the bar lists (readiness / context areas and items, cyber-risk
 * profiles). QA, 9 Oct 2026: "sort by group or by value".
 *
 *   group  — the list as the page builds it (framework order, risk order…),
 *            with its group headings
 *   desc   — every bar in one list, highest first
 *   asc    — every bar in one list, lowest first
 *
 * In the value modes the groups are flattened into one: the question "which
 * item is weakest?" is asked across the whole list, the way the Strengths /
 * Needs Attention cards already rank it. Each bar keeps its own label and gets
 * `groupLabel` (its group's name) so a row may still show where it belongs.
 * Bars without a value (not sent, or under the n floor) go last.
 */
export const BAR_SORT_MODES = [
    {value: 'group', label: 'Group order'},
    {value: 'desc', label: 'Highest first'},
    {value: 'asc', label: 'Lowest first'},
];

const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'string' ? Number(v.replace(/%\s*$/, '').trim()) : Number(v);
    return Number.isFinite(n) ? n : null;
};

/** The figure a bar is sorted by: its score / pct / value / percent; null when it has none or is suppressed. */
export const defaultValueOf = (it) => {
    if (!it || it.suppressed) return null;
    const v = it.score ?? it.pct ?? it.value ?? it.percent;
    return num(v);
};

/** One flat list of bars in the given mode (stable; the input is returned untouched in group mode). */
export const sortItems = (items, mode, getValue = defaultValueOf) => {
    if ((mode !== 'asc' && mode !== 'desc') || !Array.isArray(items)) return items;
    const mul = mode === 'desc' ? -1 : 1;
    return items
        .map((it, i) => ({it, i, v: getValue(it)}))
        .sort((a, b) => {
            if (a.v === null && b.v === null) return a.i - b.i;
            if (a.v === null) return 1;
            if (b.v === null) return -1;
            return ((a.v - b.v) * mul) || (a.i - b.i);
        })
        .map((d) => d.it);
};

const groupName = (g) => g.parentLabel || g.header || g.group || g.areaTitle || g.indicator || g.label || g.title || '';

/**
 * Groups of bars in the given mode. `groups` is the page's own shape — objects
 * holding their bars under `items` (or `rows`, or the key named in `itemsKey`)
 * plus a heading field. Group mode returns the input as is; the value modes
 * return a single group with no heading and all the bars sorted.
 */
// (The option is `getValue`, not `valueOf`: destructuring `valueOf` from `{}` would pick up Object.prototype.valueOf.)
export const sortBars = (groups, mode, {getValue = defaultValueOf, itemsKey} = {}) => {
    if ((mode !== 'asc' && mode !== 'desc') || !Array.isArray(groups) || groups.length === 0) return groups;
    const key = itemsKey || (Array.isArray(groups[0]?.items) ? 'items' : Array.isArray(groups[0]?.rows) ? 'rows' : 'items');
    const all = groups.flatMap((g) => (g[key] || []).map((it) => ({...it, groupLabel: it.groupLabel ?? groupName(g)})));
    const flat = {
        ...groups[0],
        header: null, group: null, parentLabel: '', areaTitle: '', indicator: '', label: '', title: '',
        flattened: true,
        [key]: sortItems(all, mode, getValue),
    };
    return [flat];
};
